/**
 * What sits on the home screen's insight strip: the layout entry, its stable
 * id, and the parser that reads a stored layout back safely.
 *
 * The sibling of `insight-layout.ts`, and it borrows that file's two load-
 * bearing ideas rather than restating them.
 *
 * 1. **An entry's id IS the hash of what it draws.** So the same card over the
 *    same question cannot be placed twice, and "already added" is an id lookup.
 * 2. **No stored layout is not an empty layout.** A business nobody has
 *    customized shows what its records imply, which is why `entry-repo` answers
 *    null rather than `[]` when there is no row.
 *
 * It also borrows the six mutations — append, remove, move, replace, rename,
 * resize — which are generic in the entry for exactly this reason.
 *
 * What it does NOT borrow is the spec grammar. A business spec names records,
 * people and channels; a record spec names fields. Keeping them apart is what
 * makes "a business card can never ask about a field" a fact the type system
 * enforces rather than a rule somebody has to remember.
 *
 * The one card that names a field is `{ host, record }`, and it does not bend
 * that rule either: the record half is a RECORD spec, read by the record's own
 * reader and hashed by the record's own normalisation, hosted here because a
 * total reaching two records belongs to neither of them.
 */
import {
  allowsSize,
  allowsSpan,
  defaultSizeFor,
  defaultSpanFor,
  INSIGHT_CARD_SHAPE,
  isInsightCardId,
  type InsightCardId,
  type InsightSize,
  type InsightSpan,
} from '@/lib/insight-cards';
import {
  INSIGHT_AGG_MAX_DEPTH,
  INSIGHT_AGG_MAX_LEAVES,
  INSIGHT_WHERE_MAX_VALUES,
  isFormulaCalcOp,
  readLedgerCardSpec,
} from '@alluzo/shared';

import {
  BUSINESS_FILTER_KEYS,
  BUSINESS_FILTER_OPS,
  BUSINESS_FLOWS,
  BUSINESS_GROUP_KEYS,
  businessShapeOf,
  isHostedSpec,
  isLedgerTileSpec,
  sortBusinessFilters,
  sortBusinessTree,
  type BusinessAggNode,
  type BusinessFilter,
  type BusinessFilterKey,
  type BusinessFilterOp,
  type BusinessFlow,
  type BusinessGroupKey,
  type BusinessInsightSpec,
  type BusinessKind,
  type BusinessMeasure,
  type HostedRecordSpec,
} from '@/lib/insight-business-query';
import { canonical, isLeaderSpec, isRecordSpec, normalizedSpec } from '@/lib/insight-layout';

export type BusinessInsightLayoutEntry = {
  /** `businessSpecHash(card, spec)`. Stable across launches, unique by build. */
  readonly id: string;
  readonly card: InsightCardId;
  readonly spec: BusinessInsightSpec;
  readonly span: InsightSpan;
  /**
   * The rectangle it takes on the Dashboard's grid, beside the span the strip
   * laid out by — the record layout's own field, added here when the
   * Dashboard moved onto that grid (23 Sep 2026).
   */
  readonly size: InsightSize;
  /** The catalogue key a default gave this card, such as `home.today_in`. */
  readonly labelKey: string | null;
  /** A name the person typed. Wins over `labelKey`, and outside the hash. */
  readonly title: string | null;
};

/* -------------------------------------------------------------------- hash */

export const businessSpecHash = (card: InsightCardId, spec: BusinessInsightSpec): string =>
  canonical({ card, spec });

/**
 * A spec with everything default left out, so two ways of saying the same
 * thing are the same card.
 *
 * Two defaults matter. `filters: []` is no conditions, and must hash as a spec
 * written before conditions existed. `kind: 'record'` is the default half of
 * the catalog, and the candidate generator omits it while the editor may set
 * it — left alone, those would be two cards asking one question.
 */
const normalize = (spec: BusinessInsightSpec): BusinessInsightSpec => {
  // A hosted card is a record spec, so it is normalised by the record's own
  // rule. Two normalisations of one grammar would hash the same question two
  // ways, and the id IS the question.
  if (isHostedSpec(spec)) return { host: spec.host, record: normalizedSpec(spec.record) };
  // A money figure: this month's profit is profit with no scope, so it hashes as one card.
  if (isLedgerTileSpec(spec)) return spec.scope === undefined || spec.scope === 'month' ? { ledger: spec.ledger } : { ledger: spec.ledger, scope: spec.scope };
  const filters = sortBusinessFilters(spec.filters);
  const held: Record<string, unknown> = { ...spec };
  // A combined card's totals carry conditions of their own, put in one order
  // by the same rule, so the same totals asked two ways are one card.
  if (spec.measure.agg === 'formula') held.measure = { agg: 'formula', expr: sortBusinessTree(spec.measure.expr) };
  if (filters === undefined) delete held.filters;
  else held.filters = filters;
  if (held.kind === 'record' || held.kind === undefined) delete held.kind;
  return held as BusinessInsightSpec;
};

/** One entry, with its id derived rather than passed. */
/**
 * Whether a card names a LEADER — "Top record", "Busiest day" — rather than a
 * figure. A leader's value is a name, and a name does not fit a cell, which is
 * why the record grid's `defaultLayoutFor` widens one (`insight-cards.ts`).
 */
const isBusinessLeader = (spec: BusinessInsightSpec): boolean =>
  isHostedSpec(spec) ? isLeaderSpec(spec.record) : isLedgerTileSpec(spec) ? false : spec.shape === 'scalar' && spec.leaderOf !== undefined;

/**
 * The size a card takes when nothing stored says: read off its span, the
 * record layout's migration word for word (`insight-layout.ts`) — half was one
 * cell, full was a whole row, and a breakdown at full row was always the tall
 * one — so an existing Dashboard looks the same the day it moves onto the
 * grid. **Except a leader, which is Wide** whatever its span: on the strip a
 * half-width leader truncated its name, and the grid is the chance to stop.
 */
export const sizeFromSpan = (card: InsightCardId, span: InsightSpan, spec?: BusinessInsightSpec): InsightSize => {
  if (spec !== undefined && isBusinessLeader(spec) && allowsSize(card, 'wide')) return 'wide';
  const migrated: InsightSize = span === 'half' ? 'small' : card === 'insight.breakdown' ? 'large' : 'wide';
  return allowsSize(card, migrated) ? migrated : defaultSizeFor(card);
};

/**
 * A layout entry. `size` is last and optional so every caller written before
 * the grid still builds a sound entry: with no size, the span decides it
 * (`sizeFromSpan`), which is what the strip drew.
 */
export const businessEntry = (
  card: InsightCardId,
  spec: BusinessInsightSpec,
  span: InsightSpan = defaultSpanFor(card),
  labelKey: string | null = null,
  title: string | null = null,
  size?: InsightSize,
): BusinessInsightLayoutEntry => {
  const normalized = normalize(spec);
  const placed = allowsSpan(card, span) ? span : defaultSpanFor(card);
  return {
    id: businessSpecHash(card, normalized),
    card,
    spec: normalized,
    span: placed,
    size: size !== undefined && allowsSize(card, size) ? size : sizeFromSpan(card, placed, normalized),
    labelKey,
    title: title === null || title.trim() === '' ? null : title.trim(),
  };
};

/* ------------------------------------------------------------ reading back */

const SPANS: readonly string[] = ['half', 'full'];
const SIZES: readonly string[] = ['small', 'wide', 'large'];
const SCOPES: readonly string[] = ['day', 'week', 'month', 'year', 'all'];
const KINDS: readonly string[] = ['record', 'directory'];
const MONEY_AGGS: readonly string[] = ['sum', 'avg', 'min', 'max'];

const isFilter = (value: unknown): value is BusinessFilter => {
  if (value === null || typeof value !== 'object') return false;
  const filter = value as Record<string, unknown>;
  if (!BUSINESS_FILTER_KEYS.includes(filter.key as BusinessFilterKey)) return false;
  if (!BUSINESS_FILTER_OPS.includes(filter.op as BusinessFilterOp)) return false;
  const one = (held: unknown): boolean => typeof held === 'string' && held !== '';
  // "Is any of" and "is none of" carry a list, and only they do: a card saved
  // with one value reads as it always has, and a list anywhere else is a
  // shape no build wrote.
  if (filter.op === 'in' || filter.op === 'not_in') {
    return (
      Array.isArray(filter.value) &&
      filter.value.length > 0 &&
      filter.value.length <= INSIGHT_WHERE_MAX_VALUES &&
      filter.value.every(one)
    );
  }
  return one(filter.value);
};

/**
 * One bad condition drops the WHOLE card, not just that condition.
 *
 * Dropping only the condition would leave a card called "Trading profit"
 * counting the staff payouts it was told to leave out — a different number
 * under a name that promised another. A missing card is the honest failure.
 */
const filtersAreSound = (value: unknown): boolean =>
  value === undefined || (Array.isArray(value) && value.every(isFilter));

/** A total's measure: money of one flow, or a count. */
const isPlainMeasure = (value: unknown): boolean => {
  if (value === null || typeof value !== 'object') return false;
  const measure = value as Record<string, unknown>;
  if (measure.agg === 'rows') return true;
  if (typeof measure.agg !== 'string' || !MONEY_AGGS.includes(measure.agg)) return false;
  return BUSINESS_FLOWS.includes(measure.flow as BusinessFlow);
};

const isMeasure = (value: unknown): value is BusinessMeasure => {
  if (value === null || typeof value !== 'object') return false;
  const measure = value as Record<string, unknown>;
  if (measure.agg === 'idle_days') return true;
  if (measure.agg === 'formula') return isTree(measure.expr);
  return isPlainMeasure(value);
};

/**
 * A combined card's tree, with the record tree's two caps proved: at most
 * `INSIGHT_AGG_MAX_LEAVES` totals, counted across the whole tree, and no
 * deeper than `INSIGHT_AGG_MAX_DEPTH`. One bad total drops the whole card.
 */
const isTree = (value: unknown): value is BusinessAggNode => {
  const tally = { leaves: 0 };
  return isNode(value, 1, tally) && tally.leaves > 0 && tally.leaves <= INSIGHT_AGG_MAX_LEAVES;
};

const isNode = (value: unknown, depth: number, tally: { leaves: number }): boolean => {
  if (depth > INSIGHT_AGG_MAX_DEPTH || tally.leaves > INSIGHT_AGG_MAX_LEAVES) return false;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const node = value as Record<string, unknown>;
  if (node.kind === 'agg') {
    tally.leaves += 1;
    return isPlainMeasure(node.measure) && filtersAreSound(node.filters);
  }
  if (node.kind === 'const') return typeof node.value === 'number' && Number.isFinite(node.value);
  if (node.kind === 'calc') {
    return isFormulaCalcOp(node.op) && isNode(node.left, depth + 1, tally) && isNode(node.right, depth + 1, tally);
  }
  if (node.kind === 'round') {
    return (
      typeof node.decimals === 'number' &&
      Number.isInteger(node.decimals) &&
      node.decimals >= 0 &&
      node.decimals <= 8 &&
      isNode(node.of, depth + 1, tally)
    );
  }
  return false;
};

const isGroupKey = (value: unknown): value is BusinessGroupKey =>
  BUSINESS_GROUP_KEYS.includes(value as BusinessGroupKey);

/**
 * A card hosted on the Dashboard: a record id, and that record's own spec.
 *
 * The record half goes through `isRecordSpec`, the very reader the record's
 * entry list uses. A hosted card an older build cannot read is dropped whole,
 * which is the same rule every other stored shape keeps.
 */
const isHosted = (value: unknown): value is HostedRecordSpec => {
  if (value === null || typeof value !== 'object') return false;
  const spec = value as Record<string, unknown>;
  if (typeof spec.host !== 'string' || spec.host === '') return false;
  return isRecordSpec(spec.record);
};

/**
 * Whether this app draws a pinned money figure (step 12.5 of
 * docs/plans/ledger.md): the web turns it on at start (`main.tsx`), because it
 * asks the server for the figure and draws it. The phone never does: it
 * holds no ledger lines (L14), so its readers drop such a card whole, as they
 * drop any card a newer build wrote.
 */
let ledgerTilesDrawn = false;

export const drawLedgerTiles = (on: boolean): void => {
  ledgerTilesDrawn = on;
};

export const drawsLedgerTiles = (): boolean => ledgerTilesDrawn;

/** A spec is data on disk that an older or newer build may have written. */
const isSpec = (value: unknown): value is BusinessInsightSpec => {
  if (value === null || typeof value !== 'object') return false;
  const spec = value as Record<string, unknown>;
  if (spec.host !== undefined) return isHosted(value);
  if (spec.ledger !== undefined) return ledgerTilesDrawn && readLedgerCardSpec(value) !== null;
  if (!isMeasure(spec.measure)) return false;
  if (!filtersAreSound(spec.filters)) return false;
  if (spec.kind !== undefined && !KINDS.includes(spec.kind as BusinessKind)) return false;
  const scoped = typeof spec.scope === 'string' && SCOPES.includes(spec.scope);
  switch (spec.shape) {
    case 'scalar':
      return scoped && (spec.leaderOf === undefined || isGroupKey(spec.leaderOf));
    case 'scalar_pair':
      // There is no period before all of time, and idleness has no period
      // before it at all — the compiler refuses both, so neither is stored.
      return scoped && spec.scope !== 'all' && (spec.measure as BusinessMeasure).agg !== 'idle_days';
    case 'series':
      return typeof spec.days === 'number' && Number.isFinite(spec.days);
    case 'breakdown':
      return scoped && isGroupKey(spec.by) && typeof spec.limit === 'number';
    default:
      return false;
  }
};

/**
 * The condition keys a build once wrote and this one no longer reads. A
 * "kind of record" condition named a business template; templates are gone
 * (step 2.5 of docs/plans/playbooks-and-room-bill.md), so a card that held one
 * reads as all records rather than being dropped — the condition could never
 * have narrowed anything, since no record carries a template.
 */
const RETIRED_FILTER_KEYS: readonly string[] = ['template'];

const withoutRetired = (filters: unknown): unknown => {
  if (!Array.isArray(filters)) return filters;
  const kept = filters.filter(
    (one) => !(one !== null && typeof one === 'object' && RETIRED_FILTER_KEYS.includes((one as { key?: unknown }).key as string)),
  );
  return kept.length === filters.length ? filters : kept.length === 0 ? undefined : kept;
};

const treeWithoutRetired = (node: unknown): unknown => {
  if (node === null || typeof node !== 'object' || Array.isArray(node)) return node;
  const held = node as Record<string, unknown>;
  if (held.kind === 'agg') {
    const filters = withoutRetired(held.filters);
    if (filters === held.filters) return node;
    const { filters: _gone, ...rest } = held;
    return filters === undefined ? rest : { ...rest, filters };
  }
  if (held.kind === 'calc') return { ...held, left: treeWithoutRetired(held.left), right: treeWithoutRetired(held.right) };
  if (held.kind === 'round') return { ...held, of: treeWithoutRetired(held.of) };
  return node;
};

/** A stored spec with every retired condition taken out, at the top and in a combined card's totals. */
export const specWithoutRetiredFilters = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
  const spec = value as Record<string, unknown>;
  if (spec.host !== undefined) return value;
  const { filters: held, ...rest } = spec;
  const filters = withoutRetired(held);
  const out: Record<string, unknown> = filters === undefined ? rest : { ...rest, filters };
  const measure = spec.measure as Record<string, unknown> | undefined;
  if (measure !== null && typeof measure === 'object' && measure.agg === 'formula') {
    out.measure = { ...measure, expr: treeWithoutRetired(measure.expr) };
  }
  return out;
};

/**
 * Read a stored layout, dropping anything this build cannot draw.
 *
 * Unparseable JSON answers null, which the caller treats as "no layout" and
 * falls back to what the business's records imply — the safe direction. A single
 * bad entry inside good JSON is dropped on its own, so one unknown card from a
 * newer build does not blank the strip.
 *
 * A card paired with a spec of the wrong shape is dropped too. That pair can
 * only come from a bug, and drawing it would show a permanently empty card
 * rather than saying anything.
 */
export const parseBusinessLayout = (json: string): BusinessInsightLayoutEntry[] | null => {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!Array.isArray(raw)) return null;

  const entries: BusinessInsightLayoutEntry[] = [];
  const seen = new Set<string>();
  for (const held of raw) {
    if (held === null || typeof held !== 'object') continue;
    const row = held as Record<string, unknown>;
    const spec = specWithoutRetiredFilters(row.spec);
    if (!isInsightCardId(row.card) || !isSpec(spec)) continue;
    if (INSIGHT_CARD_SHAPE[row.card] !== businessShapeOf(spec)) continue;
    const span = typeof row.span === 'string' && SPANS.includes(row.span) ? (row.span as InsightSpan) : undefined;
    // A readable size this card allows wins; without one — every layout
    // written before the Dashboard moved onto the grid — the span decides it.
    // An unreadable size drops the SIZE, never the card.
    const size = typeof row.size === 'string' && SIZES.includes(row.size) ? (row.size as InsightSize) : undefined;
    const entry = businessEntry(
      row.card,
      spec,
      span,
      typeof row.labelKey === 'string' ? row.labelKey : null,
      typeof row.title === 'string' ? row.title : null,
      size,
    );
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    entries.push(entry);
  }
  return entries;
};
